import { useState } from 'react';
import { api, ApiError, type StoredSession } from '../lib/api';

interface Props {
  readonly onSignedIn: (session: StoredSession) => void;
}

/**
 * The ordinary sign-in, then a check that the account is on the operator
 * list. An account that is not is signed straight back out — its session was
 * minted on this origin for a console it may not use, and leaving it in
 * storage would be a credential with no purpose.
 */
export const SignIn = ({ onSignedIn }: Props) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const result = await api.signIn(email.trim(), password);
      try {
        await api.whoAmI(result.token);
      } catch (caught) {
        void api.signOut(result.token).catch(() => undefined);
        if (caught instanceof ApiError && caught.status === 403) {
          throw new Error('That account is not an administrator.');
        }
        throw caught;
      }
      onSignedIn({ token: result.token, account: result.account });
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'That did not work.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="signin">
      <div className="stack-loose">
        <div>
          <p className="eyebrow">Mantelpiece</p>
          <h1 className="title">Admin</h1>
          <p className="lede">Sign in with an account on the administrator list.</p>
        </div>

        {error && (
          <p className="alert" role="alert">
            {error}
          </p>
        )}

        <form
          className="stack"
          onSubmit={(event) => {
            event.preventDefault();
            void submit();
          }}
        >
          <label className="field">
            <span>Email</span>
            <input
              className="input"
              type="email"
              value={email}
              autoComplete="username"
              autoCapitalize="off"
              spellCheck={false}
              required
              onChange={(event) => setEmail(event.target.value)}
            />
          </label>
          <label className="field">
            <span>Password</span>
            <input
              className="input"
              type="password"
              value={password}
              autoComplete="current-password"
              required
              onChange={(event) => setPassword(event.target.value)}
            />
          </label>
          <div>
            <button type="submit" className="button" disabled={busy || !email.includes('@') || !password}>
              {busy ? 'Working' : 'Sign in'}
            </button>
          </div>
        </form>
      </div>
    </main>
  );
};
